import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Stack,
  Divider,
  Paper,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
} from '@mui/material';
import {
  FileDownload as FileDownloadIcon,
  Refresh as RefreshIcon,
  Domain as RoomIcon,
  Badge as TeacherIcon,
  School as CourseIcon,
  Person as StudentIcon,
  Groups as GroupIcon,
  TableChart as TableChartIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import {
  importExportApi,
  EntitySchema,
  ExportResult,
} from '../services/importExportApi';

const getEntityIcon = (key: string) => {
  switch (key) {
    case 'rooms':
      return <RoomIcon sx={{ fontSize: 36, color: 'primary.main' }} />;
    case 'teachers':
      return <TeacherIcon sx={{ fontSize: 36, color: 'secondary.main' }} />;
    case 'students':
      return <StudentIcon sx={{ fontSize: 36, color: 'info.main' }} />;
    case 'groups':
      return <GroupIcon sx={{ fontSize: 36, color: 'warning.main' }} />;
    case 'courses':
      return <CourseIcon sx={{ fontSize: 36, color: 'success.main' }} />;
    default:
      return <TableChartIcon sx={{ fontSize: 36, color: 'primary.main' }} />;
  }
};

const ExportDataPage: React.FC = () => {
  const { t } = useLanguage();

  const [schemas, setSchemas] = useState<EntitySchema[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [exportingKey, setExportingKey] = useState<string | null>(null);

  // Fonctions d'internationalisation des entités et des champs
  const getEntityTitle = (key: string, fallback: string) => {
    const k = `academy.entity.${key}.title`;
    const translated = t(k);
    return translated !== k ? translated : fallback;
  };

  const getEntityDesc = (key: string, fallback: string) => {
    const k = `academy.entity.${key}.desc`;
    const translated = t(k);
    return translated !== k ? translated : fallback;
  };

  const getFieldLabel = (field: any) => {
    const key = field?.key || field?.fieldName;
    if (key) {
      const k = `academy.field.${key}.label`;
      const translated = t(k);
      if (translated !== k) return translated;
    }
    return field?.headerLabel || field?.label || field?.fieldName || field?.key || '';
  };

  const getDataTypeLabel = (type: string) => {
    const cleanType = String(type || '').toLowerCase();
    const k = `academy.dataType.${cleanType}`;
    const translated = t(k);
    return translated !== k ? translated : type;
  };

  // Aperçu des colonnes
  const [activeSchema, setActiveSchema] = useState<EntitySchema | null>(null);
  const [columnsDialogOpen, setColumnsDialogOpen] = useState<boolean>(false);

  const loadSchemas = async () => {
    try {
      setLoading(true);
      const data = await importExportApi.getSchemas();
      setSchemas(data);
    } catch (err: any) {
      blobticMessage.error(err.message || 'Erreur lors du chargement des entités');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSchemas();
  }, []);

  const handleExport = async (entityKey: string, format: 'xlsx' | 'csv') => {
    try {
      setExportingKey(entityKey);
      const result: ExportResult = await importExportApi.exportData(entityKey);

      if (result.rows.length === 0) {
        blobticMessage.info(
          t('academy.export.msg_no_data', getEntityTitle(result.entityKey, result.displayName))
        );
      } else {
        importExportApi.downloadExportFile(
          result,
          format,
          getEntityTitle(result.entityKey, result.displayName)
        );
        blobticMessage.success(
          t('academy.export.msg_success', result.rows.length, format.toUpperCase())
        );
      }
    } catch (err: any) {
      blobticMessage.error(err.message || "Erreur lors de l'exportation des données");
    } finally {
      setExportingKey(null);
    }
  };

  const handleOpenColumns = (schema: EntitySchema) => {
    setActiveSchema(schema);
    setColumnsDialogOpen(true);
  };

  return (
    <GenericPage
      title={t('academy.export.page_title')}
      icon={<FileDownloadIcon />}
      footerEnabled={false}
      additionalActions={
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadSchemas}
          disabled={loading}
        >
          {t('academy.export.refresh')}
        </Button>
      }
    >
      <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight={700}>
            {t('academy.export.header_title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('academy.export.header_subtitle')}
          </Typography>
        </Box>

        {loading && <LinearProgress sx={{ mb: 3 }} />}

        <Grid container spacing={3}>
          {schemas.map((s) => {
            const isCurrentlyExporting = exportingKey === s.entityKey;
            return (
              <Grid item xs={12} sm={6} md={4} key={s.entityKey}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: 2,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      boxShadow: 5,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <CardContent sx={{ flex: 1, p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {getEntityIcon(s.entityKey)}
                        <Box>
                          <Typography variant="subtitle1" fontWeight={700}>
                            {getEntityTitle(s.entityKey, s.displayName)}
                          </Typography>
                        </Box>
                      </Box>

                      <Chip
                        label={t('academy.export.lines_count', s.totalCount ?? 0)}
                        color={s.totalCount && s.totalCount > 0 ? 'primary' : 'default'}
                        sx={{ fontWeight: 700, fontSize: '0.8rem' }}
                      />
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                      {getEntityDesc(s.entityKey, s.description)}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('academy.export.columns_count', s.fields.length)}
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleOpenColumns(s)}
                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                      >
                        {t('academy.export.view_columns')}
                      </Button>
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    {isCurrentlyExporting ? (
                      <Box sx={{ py: 1 }}>
                        <Typography variant="caption" color="primary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                          {t('academy.export.generating')}
                        </Typography>
                        <LinearProgress />
                      </Box>
                    ) : (
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          color="success"
                          fullWidth
                          startIcon={<FileDownloadIcon />}
                          onClick={() => handleExport(s.entityKey, 'xlsx')}
                          disabled={isCurrentlyExporting}
                          sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                          {t('academy.export.btn_excel')}
                        </Button>
                        <Button
                          variant="outlined"
                          fullWidth
                          startIcon={<FileDownloadIcon />}
                          onClick={() => handleExport(s.entityKey, 'csv')}
                          disabled={isCurrentlyExporting}
                          sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                          {t('academy.export.btn_csv')}
                        </Button>
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Dialogue : Structure des colonnes de l'export (SANS AUCUN NOM DE TABLE SQL) */}
        <Dialog
          open={columnsDialogOpen}
          onClose={() => setColumnsDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            {t('academy.export.dialog_title', activeSchema ? getEntityTitle(activeSchema.entityKey, activeSchema.displayName) : '')}
          </DialogTitle>
          <DialogContent dividers>
            {activeSchema && (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>{t('academy.export.col_header')}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t('academy.export.col_type')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeSchema.fields.map((f) => (
                      <TableRow key={f.fieldName || f.key}>
                        <TableCell sx={{ fontWeight: 600 }}>{getFieldLabel(f)}</TableCell>
                        <TableCell>{getDataTypeLabel(f.dataType)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setColumnsDialogOpen(false)}>{t('academy.export.dialog_close')}</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </GenericPage>
  );
};

export default ExportDataPage;
